import { ErrorMessage } from '@/components/loading-error';
import { Conversation } from '@elevenlabs/client';
import { MicrophoneIcon, StopIcon } from '@heroicons/react/24/outline';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useCoachStore } from '@/store/coach';
import api from '@/utils/ky';
import { Bot, Loader2 } from 'lucide-react';
import { usePostHog } from 'posthog-js/react';
import { CUSTOMER_AGENT_ID } from '../../utils/constants';
import { AIAvatar, SpeakingAnimation } from '../role-play/components';
import { cn } from '../../lib/utils';
import { AnimatePresence, motion } from 'framer-motion';
import CustomerFeedback from './feedback';

const CUSTOMER_API_ENDPOINTS = {
  feedback: 'api/customer/sessions/feedback',
};

interface FeedbackState {
  isLoading: boolean;
  data: any;
  error: string | null;
}

export function CustomerRolePlay() {
  const navigate = useNavigate();
  const coachStore = useCoachStore();
  const posthog = usePostHog();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('sessionId');
  const rolePlaySessionRef = useRef<any>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isAISpeaking, setIsAISpeaking] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const startTimeRef = useRef<number>(0);

  const [transcript, setTranscript] = useState<
    Array<{
      source: 'ai' | 'user';
      message: string;
      timestamp: Date;
    }>
  >([]);
  const [feedbackState, setFeedbackState] = useState<FeedbackState>({
    isLoading: false,
    data: null,
    error: null
  });
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackData, setFeedbackData] = useState(null);

  // Fetch session data
  useEffect(() => {
    if (sessionId) {
      fetchSession();
    }
  }, [sessionId]);

  const fetchSession = async () => {
    try {
      setIsLoading(true);
      const data = await api.get(`api/customer/sessions/${sessionId}`).json();
      rolePlaySessionRef.current = data;

      posthog.capture('customer_roleplay_loaded', {
        sessionId,
        userId: coachStore.userId,
      });
    } catch (error: any) {
      console.error('Error fetching session:', error);
      setError(error.message || 'Failed to fetch session');
    } finally {
      setIsLoading(false);
    }
  };

  const startConversation = useCallback(async () => {
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });

      const session = rolePlaySessionRef.current;
      if (!session) {
        throw new Error('No session data found');
      }

      // Customize the prompt for customer service scenarios
      let prompt = session.voiceInstructions?.chat || '';
      if (session.aiConfig?.initialPrompt) {
        prompt += `
        IMPORTANT CONTEXT FOR CUSTOMER SERVICE ROLE-PLAY:
        - You are a customer with specific complaints or issues
        - Express realistic frustration and concerns
        - Respond to service quality and resolution attempts
        - Ask clarifying questions about solutions
        - React naturally to the service approach
        ${session.aiConfig.initialPrompt}
        You are talking to ${session.user?.name} in English.
        `;
      }
      if (session.scenario?.customerContex) {
        prompt += `Context about the customer (you):
        ${session.scenario.customerContext}
        `;
      }

      const conversation = await Conversation.startSession({
        agentId: CUSTOMER_AGENT_ID,
        connectionType: 'webrtc',
        overrides: {
          agent: {
            prompt: {
              prompt,
            },
            firstMessage: "Hello, I need help with something...",
            language: 'en',
          },
        },
        onConnect: () => {
          console.log('Connected to ElevenLabs Customer Service Agent');
          posthog.capture('customer_service_practice_started', {
            sessionId,
            userId: coachStore.userId,
          });
          setIsAISpeaking(true);
          setIsConnected(true);
        },
        onDisconnect: () => {
          console.log('Disconnected from ElevenLabs Customer Service Agent');
          setIsConnected(false);
          setIsAISpeaking(false);
        },
        onError: (error) => {
          console.error('ElevenLabs Error:', error);
          setError(error || 'Connection Error');
        },
        onModeChange: ({ mode }) => {
          setIsAISpeaking(mode === 'speaking');
        },
        onMessage({ source, message }) {
          setTranscript((prev) => [
            ...prev,
            {
              source,
              message,
              timestamp: new Date(),
            },
          ]);
        },
      });
      setConversation(conversation);
    } catch (error: any) {
      console.error('Failed to start conversation:', error);
      setError(error.message || 'Failed to start conversation');
    }
  }, [conversation, sessionId]);

  const stopConversation = useCallback(async () => {
    try {
      setFeedbackState(prev => ({ ...prev, isLoading: true }));

      await conversation?.endSession();

      // Format transcript for feedback
      const formattedTranscript = transcript
        .map(
          (msg) =>
            `${msg.source === 'ai' ? 'Customer' : 'Agent'}: ${msg.message}`,
        )
        .join('\n');

      const result: any = await api
        .post(CUSTOMER_API_ENDPOINTS.feedback, {
          timeout: 90000,
          retry: { limit: 2, methods: ['post'] },
          json: {
            sessionId,
            transcript: formattedTranscript,
            duration: (Date.now() - startTimeRef.current) / 1000,
          },
        })
        .json();

      // Update store and state
      coachStore.setLastSessionId(sessionId);
      coachStore.setLastFeedback(result);
      coachStore.setSessionTranscript(formattedTranscript);

      setFeedbackState({
        isLoading: false,
        data: result,
        error: null
      });
      setFeedbackData(result);
      setShowFeedback(true);
    } catch (error: any) {
      console.error('Failed to end conversation:', error);
      setFeedbackState(prev => ({
        ...prev,
        isLoading: false,
        error: error.message || 'Failed to generate feedback'
      }));
      toast.error('Failed to generate feedback. Please try again.');
    }
  }, [conversation, sessionId, transcript]);

  const handlePracticeAgain = () => {
    setShowFeedback(false);
    window.location.reload();
  };

  const handleDiscussFeedback = () => {
    navigate(
      `/customer/chat${
        searchParams.get('code') ? '?code=' + searchParams.get('code') : ''
      }`,
      {
        state: { fromRolePlay: true },
      },
    );
  };

  if (isLoading) return <LoadingStateView />;
  if (error) return <ErrorMessage message={error} />;
  if (!rolePlaySessionRef.current)
    return <ErrorMessage message="No session data found" />;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <Header rolePlaySession={rolePlaySessionRef.current} />

      <AnimatePresence mode="wait">
        {showFeedback ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <CustomerFeedback
              sessionId={sessionId!}
              feedback={feedbackData}
              onPracticeAgain={handlePracticeAgain}
              onDiscussFeedback={handleDiscussFeedback}
            />
          </motion.div>
        ) : (
          <motion.main
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="container mx-auto px-4 py-8"
          >
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <LeftPanel
                rolePlaySession={rolePlaySessionRef.current}
                conversation={conversation}
                onStartPractice={startConversation}
                onEndPractice={stopConversation}
                isConnected={isConnected}
              />

              <div className="lg:col-span-2">
                <RolePlayArea
                  isAISpeaking={isAISpeaking}
                  conversation={conversation}
                  onEndPractice={stopConversation}
                  isConnected={isConnected}
                  scenario={rolePlaySessionRef.current?.scenario}
                  isLoadingFeedback={feedbackState.isLoading}
                  transcript={transcript}
                />
              </div>
            </div>
          </motion.main>
        )}
      </AnimatePresence>
    </div>
  );
}

const Header = ({ rolePlaySession }: { rolePlaySession: any }) => (
  <header className="bg-white border-b border-gray-200 px-4 py-4">
    <div className="container mx-auto flex items-center justify-between">
      <img
        src="https://coach.hupo.co/logos/trademarked/orange/logo.svg"
        alt="Support Coach"
        className="h-8"
      />
      <h1 className="text-xl font-semibold text-gray-900">
        {rolePlaySession.scenario.title}
      </h1>
    </div>
  </header>
);

const LeftPanel = ({
  rolePlaySession,
  conversation,
  onStartPractice,
  onEndPractice,
  isConnected,
}: {
  rolePlaySession: any;
  conversation: any;
  onStartPractice: () => void;
  onEndPractice: () => void;
  isConnected: boolean;
}) => (
  <div className="bg-white rounded-xl shadow-sm p-6 space-y-6">
    <section>
      <h2 className="text-lg font-semibold mb-3">Service Framework</h2>
      <p className="text-gray-600 mb-4">{rolePlaySession.framework.title}</p>
      <p className="text-gray-600 text-sm">
        {rolePlaySession.framework.description}
      </p>
    </section>

    <section>
      <h2 className="text-lg font-semibold mb-3">Service Objective</h2>
      <p className="text-gray-600">{rolePlaySession.scenario.objective}</p>
    </section>

    <section>
      <h2 className="text-lg font-semibold mb-3">Customer Context</h2>
      <p className="text-gray-600 text-sm">
        {rolePlaySession.scenario.customerContext}
      </p>
    </section>

    <div className="pt-4 border-t border-gray-200">
      <ControlButtons
        conversation={conversation}
        onStartPractice={onStartPractice}
        onEndPractice={onEndPractice}
        isConnected={isConnected}
      />
    </div>
  </div>
);

interface RolePlayAreaProps {
  isAISpeaking: boolean;
  conversation: any;
  onEndPractice: () => void;
  isConnected: boolean;
  scenario: any;
  isLoadingFeedback: boolean;
  transcript: Array<{
    source: 'ai' | 'user';
    message: string;
    timestamp: Date;
  }>;
}

const RolePlayArea = ({
  isAISpeaking,
  conversation,
  onEndPractice,
  isConnected,
  scenario,
  isLoadingFeedback,
  transcript
}: RolePlayAreaProps) => (
  <div className="bg-white rounded-xl shadow-sm p-8">
    <div className="mb-6">
      <h3 className="text-lg font-semibold text-gray-900">
        {scenario?.title}
      </h3>
      <p className="text-sm text-gray-600 mt-2">
        {scenario?.introduction}
      </p>
    </div>

    <div className="flex flex-col items-center justify-center min-h-[400px]">
      {isLoadingFeedback ? (
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Analyzing Service Performance
          </h3>
          <p className="text-gray-500">
            Generating detailed feedback on customer interaction...
          </p>
        </div>
      ) : isConnected ? (
        <div className="max-w-2xl">
          <div className="relative">
            <AIAvatar isAISpeaking={isAISpeaking} />
            <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-50 text-primary-700">
                {isAISpeaking ? 'Customer is speaking...' : 'Customer is listening...'}
              </span>
            </div>
          </div>

          <div className="text-center mt-16">
            <button
              onClick={onEndPractice}
              disabled={isLoadingFeedback}
              className="px-4 py-2 rounded-lg bg-red-50 text-red-600 font-medium hover:bg-red-100 transition-colors disabled:opacity-50"
            >
              End Practice
            </button>
          </div>
        </div>
      ) : (
        <div className="text-center">
          <Bot className="mx-auto h-16 w-16 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Ready to practice customer service?
          </h3>
          <p className="text-gray-500 mb-6">
            Click start to begin your service interaction with a simulated customer
          </p>
        </div>
      )}
    </div>
  </div>
);

const ControlButtons = ({
  conversation,
  onStartPractice,
  onEndPractice,
  isConnected,
}: {
  conversation: any;
  onStartPractice: () => void;
  onEndPractice: () => void;
  isConnected: boolean;
}) => (
  <div className="flex justify-center gap-4">
    {!isConnected ? (
      <button
        onClick={onStartPractice}
        className="flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary transition-colors"
      >
        <MicrophoneIcon className="h-5 w-5" />
        Start Practice
      </button>
    ) : (
      <button
        onClick={onEndPractice}
        className="flex items-center gap-2 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
      >
        <StopIcon className="h-5 w-5" />
        End Practice
      </button>
    )}
  </div>
);

const LoadingStateView = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary-600 mx-auto mb-4" />
      <p className="text-gray-600">Loading your service practice session...</p>
    </div>
  </div>
);